import { API_ORIGIN, apiUrl } from "./apiOrigin";
// OrgSettings.js — Organisation-wide financial assumptions consumed by the
// Business Insights engine. Values are entered in POUNDS, stored in integer PENCE.
//
// Two modes:
//   • Standalone (default)  — its own heading and Save button. This is how the
//     old Settings -> Organisation page rendered it; the `org_settings` route
//     is kept intact even though the nav button has gone.
//   • Embedded (`embedded`) — heading and Save button hidden, because it now
//     lives as the "Configurables" tab inside the Settings dialog and is saved
//     by that dialog's own "Save changes" button. The parent drives it through
//     a ref:
//         const r = useRef(null);
//         <OrgSettings ref={r} embedded onDirty={() => setHasChanges(true)} />
//         await r.current.save();     // resolves, or throws with a message
//     `save()` is a no-op unless the settings actually loaded AND something was
//     edited, so saving an untouched tab can never write stale figures back.

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
const API = apiUrl("");
const ROLES = [["PHARMACIST", "Pharmacist"], ["PRE_REG", "Pre-Reg Pharmacist"], ["TECHNICIAN", "Technician"], ["DISPENSER", "Dispenser"], ["COUNTER_STAFF", "Counter Staff"], ["CLEANING_STAFF", "Cleaning Staff"], ["DRIVER", "Driver"], ["MANAGER", "Manager"], ["GROUP_MANAGER", "Group Manager"]];
function authHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
}
const toPounds = pence => pence === null || pence === undefined || pence === "" ? "" : (Number(pence) / 100).toFixed(2);
const toPence = pounds => {
  const n = Number(String(pounds).replace(/[£,\s]/g, ""));
  return isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};
const help = {
  fontSize: 11,
  color: "#5D5D5D",
  marginTop: 2,
  lineHeight: 1.4
};
const label = {
  fontSize: 12,
  fontWeight: 700,
  color: "#434343",
  textTransform: "uppercase",
  letterSpacing: 0.3
};
const input = {
  fontSize: 13,
  padding: "6px 10px",
  border: "1px solid #8C8C8C",
  borderRadius: 4,
  width: 120
};
const section = {
  border: "1px solid #F0F0F0",
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 16,
  background: "#fff"
};
const OrgSettings = forwardRef(function OrgSettings({
  embedded = false,
  onDirty
} = {}, ref) {
  const [org, setOrg] = useState("");
  const [payRates, setPayRates] = useState({}); // ROLE_KEY -> pounds string
  const [avgItem, setAvgItem] = useState("");
  const [premises, setPremises] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [locumDay, setLocumDay] = useState("");
  const [fees, setFees] = useState([]); // { key,label,matchText,fee (pounds string) }
  const [status, setStatus] = useState("loading"); // loading | ready | saving | saved | error
  const [error, setError] = useState(null);

  // Refs, not state, so the imperative save() always reads current values
  // rather than whatever was captured when the handle was created.
  const loadedRef = useRef(false);
  const dirtyRef = useRef(false);
  const valuesRef = useRef({});
  valuesRef.current = {
    payRates,
    avgItem,
    premises,
    vehicle,
    locumDay,
    fees,
    org
  };

  // Any edit marks the tab dirty and tells the parent dialog it has changes.
  const touch = () => {
    if (!dirtyRef.current) dirtyRef.current = true;
    if (onDirty) onDirty();
  };
  useEffect(() => {
    (async () => {
      try {
        const isAll = (localStorage.getItem("organisationName") || "") === "Pharmace all";
        const body = isAll ? {
          organisationName: localStorage.getItem("bi_targetOrg") || ""
        } : {};
        const res = await fetch(`${API}/api/business/settings/get`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setOrg(data.organisationName);
        const s = data.settings || {};
        const pr = {};
        for (const [k] of ROLES) pr[k] = toPounds(s.payRates?.[k]);
        setPayRates(pr);
        setAvgItem(toPounds(s.avgItemIncomePence));
        setPremises(toPounds(s.premisesMonthlyPence));
        setVehicle(toPounds(s.vehicleCostPerRoundPence));
        setLocumDay(toPounds(s.locumDayRatePence));
        const baseFees = s.serviceFees && s.serviceFees.length ? s.serviceFees : (data.serviceDefs || []).map(d => ({
          key: d.key,
          label: d.label,
          matchText: d.matchText,
          feePence: d.defaultFeePence
        }));
        setFees(baseFees.map(f => ({
          key: f.key,
          label: f.label,
          matchText: f.matchText || "",
          fee: toPounds(f.feePence)
        })));
        loadedRef.current = true;
        setStatus("ready");
      } catch (e) {
        setError(e.message);
        setStatus("error");
      }
    })();
  }, []);

  // The actual write. Shared by the standalone button and the parent dialog.
  const commit = async () => {
    const v = valuesRef.current;
    const settings = {
      payRates: Object.fromEntries(ROLES.map(([k]) => [k, toPence(v.payRates[k])]).filter(([, val]) => val)),
      avgItemIncomePence: toPence(v.avgItem),
      premisesMonthlyPence: toPence(v.premises),
      vehicleCostPerRoundPence: toPence(v.vehicle),
      locumDayRatePence: toPence(v.locumDay),
      serviceFees: v.fees.filter(f => f.key && f.label).map(f => ({
        key: f.key,
        label: f.label,
        matchText: f.matchText,
        feePence: toPence(f.fee) || 0
      }))
    };
    const isAll = (localStorage.getItem("organisationName") || "") === "Pharmace all";
    const body = {
      settings,
      ...(isAll ? {
        organisationName: v.org
      } : {})
    };
    const res = await fetch(`${API}/api/business/settings/save`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    dirtyRef.current = false;
  };
  const save = async () => {
    setStatus("saving");
    setError(null);
    try {
      await commit();
      setStatus("saved");
      setTimeout(() => setStatus("ready"), 1800);
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  };

  // Parent-driven save. Unlike the button above this RE-THROWS, so the Settings
  // dialog can report the failure alongside its other save errors.
  useImperativeHandle(ref, () => ({
    isDirty: () => dirtyRef.current,
    isLoaded: () => loadedRef.current,
    save: async () => {
      if (!loadedRef.current || !dirtyRef.current) return {
        skipped: true
      };
      setStatus("saving");
      setError(null);
      try {
        await commit();
        setStatus("ready");
        return {
          saved: true
        };
      } catch (e) {
        setError(e.message);
        setStatus("error");
        throw e;
      }
    }
  }), []);
  const setFee = (i, field, v) => {
    touch();
    setFees(fs => fs.map((f, j) => j === i ? {
      ...f,
      [field]: v
    } : f));
  };
  if (status === "loading") return <div style={{
    padding: 24,
    color: "#5D5D5D"
  }}>Loading organisation settings…</div>;
  return <div style={embedded ? {
    padding: 0,
    maxWidth: 820
  } : {
    padding: "16px 20px",
    maxWidth: 820
  }}>
      {!embedded && <h2 style={{
      margin: "0 0 4px",
      fontSize: 20
    }}>Organisation settings</h2>}
      <div style={{
      fontSize: 13,
      color: "#5D5D5D",
      marginBottom: 16
    }}>
        Financial assumptions for <b>{org}</b>, used by the Business Insights analysis.
        Money is entered in pounds.
      </div>
      {error && <div style={{
      color: "#CF1322",
      fontSize: 13,
      marginBottom: 12
    }}>Error: {error}</div>}

      <div style={section}>
        <div style={{
        ...label,
        marginBottom: 10
      }}>Typical hourly pay per role (£/hour)</div>
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
        gap: 10
      }}>
          {ROLES.map(([k, name]) => <label key={k} style={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
              <span style={{
            fontSize: 12,
            color: "#434343"
          }}>{name}</span>
              <input style={input} value={payRates[k] || ""} placeholder="£/hr" onChange={e => {
            touch();
            setPayRates(p => ({
              ...p,
              [k]: e.target.value
            }));
          }} />
            </label>)}
        </div>
        <div style={help}>Typical rate for each role. When staff profiles are complete, rates can be assigned individually and will override these figures. Roles left blank are excluded from staff-cost calculations (flagged, not zeroed).</div>
      </div>

      <div style={section}>
        <div style={{
        ...label,
        marginBottom: 10
      }}>Income assumptions</div>
        <div style={{
        display: "flex",
        gap: 24,
        flexWrap: "wrap"
      }}>
          <label style={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
            <span style={{
            fontSize: 12,
            color: "#434343"
          }}>Average income per item (£)</span>
            <input style={input} value={avgItem} placeholder="e.g. 1.85" onChange={e => {
            touch();
            setAvgItem(e.target.value);
          }} />
            <div style={{
            ...help,
            maxWidth: 300
          }}>Organisation-wide average for now — it varies between sites; per-site values will follow.</div>
          </label>
        </div>
        <div style={{
        ...label,
        margin: "14px 0 8px"
      }}>Service fees (£ per completed service)</div>
        <table style={{
        borderCollapse: "collapse",
        width: "100%"
      }}>
          <thead><tr>
            {["Service", "Match text (appointments)", "Fee £", ""].map(h => <th key={h} style={{
              textAlign: "left",
              fontSize: 11,
              color: "#5D5D5D",
              padding: "4px 8px",
              borderBottom: "1px solid #F0F0F0"
            }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {fees.map((f, i) => <tr key={i}>
                <td style={{
              padding: "4px 8px"
            }}><input style={{
                ...input,
                width: 180
              }} value={f.label} onChange={e => setFee(i, "label", e.target.value)} /></td>
                <td style={{
              padding: "4px 8px"
            }}><input style={{
                ...input,
                width: 180
              }} value={f.matchText} onChange={e => setFee(i, "matchText", e.target.value)} /></td>
                <td style={{
              padding: "4px 8px"
            }}><input style={{
                ...input,
                width: 90
              }} value={f.fee} onChange={e => setFee(i, "fee", e.target.value)} /></td>
                <td style={{
              padding: "4px 8px"
            }}>
                  <button onClick={() => {
                touch();
                setFees(fs => fs.filter((_, j) => j !== i));
              }} style={{
                border: "1px solid #D9D9D9",
                background: "#fff",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 12,
                padding: "4px 8px"
              }}>✕</button>
                </td>
              </tr>)}
          </tbody>
        </table>
        <button onClick={() => {
        touch();
        setFees(fs => [...fs, {
          key: `svc_${Date.now()}`,
          label: "",
          matchText: "",
          fee: ""
        }]);
      }} style={{
        marginTop: 8,
        border: "1px solid #0E8CFF",
        color: "#0E8CFF",
        background: "#fff",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 12,
        padding: "5px 10px"
      }}>
          + Add service</button>
        <div style={help}>Fee credited per completed service. Counted from the services module (matched against appointment names), OR — for sites recording services on paper — from the monthly "Services delivered" periodic task entries, which take priority for that service and month. Per-site and per-service refinement will follow.</div>
      </div>

      <div style={section}>
        <div style={{
        ...label,
        marginBottom: 10
      }}>Cost assumptions</div>
        <div style={{
        display: "flex",
        gap: 24,
        flexWrap: "wrap"
      }}>
          <label style={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
            <span style={{
            fontSize: 12,
            color: "#434343"
          }}>Premises cost per branch (£/month)</span>
            <input style={input} value={premises} onChange={e => {
            touch();
            setPremises(e.target.value);
          }} />
            <div style={{
            ...help,
            maxWidth: 260
          }}>Organisation-wide average (rent, rates, utilities) applied to every branch for now; per-branch values will follow.</div>
          </label>
          <label style={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
            <span style={{
            fontSize: 12,
            color: "#434343"
          }}>Locum default day rate (£)</span>
            <input style={input} value={locumDay} onChange={e => {
            touch();
            setLocumDay(e.target.value);
          }} />
            <div style={{
            ...help,
            maxWidth: 260
          }}>Used only when a rota placement has no rate recorded — placements with actual rates always take precedence.</div>
          </label>
          <label style={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
            <span style={{
            fontSize: 12,
            color: "#434343"
          }}>Delivery vehicle cost per round (£)</span>
            <input style={input} value={vehicle} onChange={e => {
            touch();
            setVehicle(e.target.value);
          }} />
            <div style={{
            ...help,
            maxWidth: 260
          }}>Vehicle running cost per delivery round; driver time is costed separately from attendance hours × the Driver rate. Applied once a rounds count source is connected.</div>
          </label>
        </div>
      </div>

      {!embedded && <button onClick={save} disabled={status === "saving"} style={{
      border: "none",
      background: "#0E8CFF",
      color: "#fff",
      borderRadius: 6,
      padding: "10px 22px",
      fontSize: 14,
      fontWeight: 700,
      cursor: status === "saving" ? "wait" : "pointer"
    }}>
          {status === "saving" ? "Saving…" : status === "saved" ? "✓ Saved" : "Save organisation settings"}
        </button>}
      <div style={{
      ...help,
      marginTop: 8
    }}>
        {embedded ? "Saved with the dialog's Save changes button. Changes apply to the next Business Insights computation (use Refresh on the Business Insights page to recompute a month immediately)." : "Changes apply to the next Business Insights computation (use the Refresh button on the Business Insights page to recompute a month immediately)."}
      </div>
    </div>;
});
export default OrgSettings;
